import React, { useEffect, useMemo, useRef, useState } from "react";

import { FoodDetailDrawer } from "./FoodDetailDrawer";
import { FoodDetailPanel } from "./FoodDetailPanel";
import { LabelLink } from "./LabelLink";
import {
  blockingConcerns,
  describeConcern,
  explainConcern,
  hasBlockingConcern,
} from "./concerns";
import {
  Badge,
  GroundingPanel,
  JsonBlock,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  Toast,
  upcOf,
} from "./shared";
import {
  ExistingMatch,
  FoodLabelLink,
  IngestLabelRef,
  IngestReviewItem,
  LabelMatch,
  MergeResult,
  PassioFood,
} from "./types";
import {
  CatalogApi,
  extractApiError,
  extractMergeResults,
  isDuplicateUpcError,
} from "./useCatalogApi";

interface ItemState {
  selected: boolean;
  effectiveAction: "create" | "update" | "blocked";
  effectivePayload: PassioFood;
  targetId: string | null;
  /** create but the UPC already exists -> hard duplicate, must merge instead. */
  upcConflict: boolean;
  /** The existing record this item duplicates (by UPC), if any. */
  dupMatch?: ExistingMatch | null;
  /** Collapsed card body (header stays visible). */
  collapsed?: boolean;
  /** Inline editor open (controlled so the sticky header owns the toggle). */
  editing?: boolean;
  edited?: boolean;
  /** Edited since the last re-check -> auto re-check when the editor is closed. */
  pendingRecheck?: boolean;
  rechecking?: boolean;
  recheckMsg?: string;
  mergeRunning?: boolean;
  mergeError?: string;
  mergeResult?: MergeResult;
  mergeApplied?: boolean;
  /**
   * Concern codes re-computed on the MERGED result (the merge chain produces new
   * data, so its sanity checks must be re-run before we display/write it).
   */
  mergeConcerns?: number[];
  /** True when the re-checked merged result carries a blocking concern. */
  mergeBlocking?: boolean;
  /** The full existing record (fetched for compare / merge). */
  mergeExisting?: PassioFood | null;
  loadingExisting?: boolean;
  /** Which variant the duplicate studio is showing. */
  mergeView?: "existing" | "new" | "merged";
  /** Labels chosen/accepted for this item; linked after write (create path). */
  chosenLabels?: FoodLabelLink[];
  /** Auto-matched label suggestions from the automatch endpoint. */
  labelSuggestions?: LabelMatch[];
  /** User threw this item out of the review queue (recoverable via Restore). */
  discarded?: boolean;
  /** A single-item re-run (ingest chain) is in flight for this item. */
  retrying?: boolean;
  /** The background retry job id (so we can watch it to completion / clear it). */
  retryJobId?: string;
  /** Error from the last retry attempt (shown inline). */
  retryError?: string;
  outcome?: {
    status: "ok" | "blocked" | "failed";
    id?: string;
    error?: string;
  };
}

/**
 * Per-item retry wiring (from the Catalog Manager, backed by the background-jobs
 * system). Lets a reviewer re-run the ingest chain for one item to try to fill in
 * missing info (e.g. nutrition), replacing the row in-place when it finishes.
 */
export interface ReviewRetryApi {
  /** Start a single-item re-run; returns the background job id. */
  start: (
    foodInput: string,
    label: string,
    itemIndex: number
  ) => Promise<string>;
  /** In-flight/finished retry jobs for THIS run (already scoped by executionId). */
  jobs: {
    id: string;
    status: string;
    error?: string;
    resultItems?: IngestReviewItem[];
    retryItemIndex?: number;
  }[];
  /** Drop a retry job once we've consumed/failed it. */
  clear: (jobId: string) => void;
  /** Persist the replaced item onto the origin run (survives reload/re-open). */
  persist: (itemIndex: number, newItem: IngestReviewItem) => void;
}

interface ReviewTabProps {
  api: CatalogApi;
  mergeChainId: string;
  reviewItems: IngestReviewItem[];
  /**
   * The EasyAI chain execution that produced these items. Passed to create/
   * update so the record's origin id links back to its run history on easyai.
   */
  executionId?: string;
  customerName?: string;
  /** Kick off the AI merge for every duplicate as soon as the page loads. */
  autoMerge?: boolean;
  /**
   * Report items written to the catalog so the originating ingest run's stored
   * history reflects them (e.g. "written 3/8"). Called after each write pass
   * with the review-item keys that succeeded and their new/updated ids.
   */
  onItemsWritten?: (written: { key: string; id?: string }[]) => void;
  /**
   * Review-item keys already written to the catalog on a prior visit to this
   * run (persisted on the ingest job). Used to re-seed the "written" state when
   * the review view remounts (e.g. after switching tabs) so a written item
   * doesn't reappear as still-to-write.
   */
  writtenKeys?: string[];
  /** Optional per-item retry wiring (background single-item re-run). */
  retry?: ReviewRetryApi;
}

const keyFor = (item: IngestReviewItem, i: number): string =>
  item.__key ||
  `${upcOf(item.ingest_payload || item.proposed) || item.name || "item"}-${i}`;

const hasContent = (food?: PassioFood | null): boolean =>
  !!food && Object.keys(food).length > 0;

// Normalize a barcode for comparison: digits only, then strip leading zeros so
// UPC-A (12) / EAN-13 (13) / GTIN-14 (14) paddings of the SAME code line up
// (e.g. "0685239665697" == "685239665697" == "00685239665697").
const normUpc = (s?: string | null): string =>
  (s || "").replace(/\D/g, "").replace(/^0+/, "");

/**
 * Find the existing catalog record this candidate duplicates by UPC. Prefers
 * the backend-provided exact match; otherwise cross-checks the candidate's
 * (possibly research-discovered) UPC against the near-match list — the case the
 * backend's node-2 lookup misses when the input had no UPC.
 */
const findUpcDuplicate = (
  item: IngestReviewItem,
  candidate: PassioFood
): ExistingMatch | null => {
  if (item.exact_upc_match) return item.exact_upc_match;
  const code = normUpc(upcOf(candidate));
  if (!code) return null;
  return (
    (item.existing_matches || []).find(
      (m) => normUpc(m.product_code) === code
    ) || null
  );
};

/** Normalize an ingest label ref ({labelId, name} | {labelId, displayName}). */
const refToLink = (r: IngestLabelRef): FoodLabelLink => ({
  labelId: r.labelId,
  displayName: r.displayName || r.name || "",
});

/** Ingest label option -> LabelMatch (for the LabelLink "Options" chips). */
const refToMatch = (r: IngestLabelRef): LabelMatch => ({
  labelId: r.labelId,
  displayName: r.displayName || r.name || "",
  score: 0,
});

/** Dedupe label links by labelId, keeping first occurrence. */
const dedupeLinks = (links: FoodLabelLink[]): FoodLabelLink[] => {
  const seen = new Set<string>();
  const out: FoodLabelLink[] = [];
  for (const l of links) {
    if (!l?.labelId || seen.has(l.labelId)) continue;
    seen.add(l.labelId);
    out.push(l);
  }
  return out;
};

/** Synthesize an ExistingMatch from a fetched full food (for the merge/dup UI). */
const matchFromFood = (food: PassioFood): ExistingMatch => ({
  id: food.id,
  name: food.name,
  brand: food.brandName || food.branded?.owner,
  product_code: upcOf(food) || undefined,
});

// ---- "did the merge actually change anything?" ------------------------------
// A merge is a *no-op* when the AI-merged record is materially identical to the
// existing catalog record — i.e. everything it kept came from existing and
// writing it back would change nothing. We compare a canonical signature of the
// meaningful content only, ignoring volatile/administrative fields (id, origin,
// timestamps, popularity, icon) and normalizing order/rounding.
const roundAmt = (n: unknown): number | null =>
  typeof n === "number" && isFinite(n) ? Math.round(n * 1000) / 1000 : null;

const foodContentSignature = (food?: PassioFood | null): string => {
  if (!food) return "";
  const nutrients = (food.nutrients || [])
    .map(
      (n) =>
        `${(n.nutrient?.name || "").trim().toLowerCase()}|${roundAmt(
          n.amount
        )}|${(n.nutrient?.unit || "").trim().toLowerCase()}`
    )
    .sort();
  const portions = (food.portions || [])
    .map(
      (p) =>
        `${(p.name || "").trim().toLowerCase()}|${roundAmt(p.weight?.value)}|${(
          p.weight?.unit || ""
        )
          .trim()
          .toLowerCase()}|${roundAmt(p.quantity)}`
    )
    .sort();
  const sig = {
    name: (food.name || "").trim().toLowerCase(),
    brand: (food.brandName || food.branded?.owner || "").trim().toLowerCase(),
    upc: normUpc(upcOf(food)),
    country: (food.branded?.country || "").trim().toLowerCase(),
    ingredients: (food.branded?.ingredients || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " "),
    quality: (food.qualityScore || "").trim().toLowerCase(),
    nutrients,
    portions,
    tags: [...(food.tags || [])].map((t) => t.toLowerCase()).sort(),
    concerns: [...(food.concerns || [])].sort((a, b) => a - b),
    webLinks: [...(food.webLinks || [])].map((w) => w.trim()).sort(),
  };
  return JSON.stringify(sig);
};

/** True when applying the merged record would not change the existing one. */
const mergeIsNoOp = (
  existing?: PassioFood | null,
  merged?: PassioFood | null
): boolean => {
  if (!existing || !merged) return false;
  return foodContentSignature(existing) === foodContentSignature(merged);
};

// ---- grouping ---------------------------------------------------------------
type GroupId = "ready" | "attention" | "duplicate" | "written" | "discarded";

const groupOf = (st: ItemState): GroupId => {
  if (st.discarded) return "discarded";
  if (st.outcome?.status === "ok") return "written";
  if (st.upcConflict && st.dupMatch) return "duplicate";
  if (st.effectiveAction === "blocked") return "attention";
  return "ready";
};

// Order the sections top-to-bottom and give each a look + copy.
const GROUP_ORDER: GroupId[] = [
  "ready",
  "duplicate",
  "attention",
  "written",
  "discarded",
];
const GROUP_META: Record<
  GroupId,
  { title: string; icon: string; ring: string; dot: string; text: string }
> = {
  ready: {
    title: "Ready to write",
    icon: "✓",
    ring: "border-green-300 bg-green-50",
    dot: "bg-green-500",
    text: "text-green-800",
  },
  duplicate: {
    title: "Already in the catalog",
    icon: "⛔",
    ring: "border-red-300 bg-red-50",
    dot: "bg-red-500",
    text: "text-red-800",
  },
  attention: {
    title: "Needs attention",
    icon: "⚠",
    ring: "border-amber-300 bg-amber-50",
    dot: "bg-amber-500",
    text: "text-amber-800",
  },
  written: {
    title: "Done — written to the catalog",
    icon: "✅",
    ring: "border-green-300 bg-green-50",
    dot: "bg-green-500",
    text: "text-green-800",
  },
  discarded: {
    title: "Discarded",
    icon: "🗑",
    ring: "border-gray-300 bg-gray-50",
    dot: "bg-gray-400",
    text: "text-gray-600",
  },
};

/** Plain-English, idiot-proof status line for a single review item. */
const itemStatus = (
  st: ItemState,
  isDuplicate: boolean
): { text: string; cls: string } => {
  if (st.outcome?.status === "ok") {
    return {
      text: `✓ Written to the catalog (id ${st.outcome.id}).`,
      cls: "text-green-700",
    };
  }
  if (st.outcome) {
    return { text: `✕ ${st.outcome.error}`, cls: "text-red-700" };
  }
  if (isDuplicate) {
    if (mergeIsNoOp(st.mergeExisting, st.mergeResult?.merged)) {
      return {
        text: "✓ Already up to date — the merge matches the existing record, so there's nothing new to write. You can skip it.",
        cls: "text-sky-700",
      };
    }
    return {
      text: "⛔ Already in the catalog (same UPC). Compare & merge with the existing record below, or skip it.",
      cls: "text-red-700",
    };
  }
  if (st.effectiveAction === "blocked") {
    return {
      text: "⚠ Needs attention before it can be written — edit the flagged fields below, then click Done to re-check.",
      cls: "text-amber-700",
    };
  }
  if (st.effectiveAction === "update") {
    return {
      text: "↻ Updates an existing record. Data looks good — tick it and write.",
      cls: "text-blue-700",
    };
  }
  return {
    text: "✓ New product, data looks good — tick it and write.",
    cls: "text-green-700",
  };
};

/**
 * One central "what's stopping this from being written" box per item. Folds the
 * duplicate flag, every blocking concern (with a data-specific explanation), and
 * the missing-UPC advisory into a single checklist so nothing is scattered.
 */
const ItemIssues: React.FC<{
  st: ItemState;
  proposed: PassioFood;
  reasons?: string[];
  /** The duplicate is already explained by the merge studio below; skip it here. */
  hideDuplicate?: boolean;
  onOpenMatch: (m: ExistingMatch) => void;
}> = ({ st, proposed, reasons, hideDuplicate, onOpenMatch }) => {
  if (st.outcome?.status === "ok") return null;

  const codes = Array.isArray(proposed.concerns) ? proposed.concerns : [];
  const blockCodes = blockingConcerns(codes);
  const isDup = st.upcConflict && !!st.dupMatch;
  const showDup = isDup && !hideDuplicate;
  // A block with no concern codes / dup (e.g. "no converted record") — surface
  // the decision's own reasons so we never show a false "ready".
  const otherBlock =
    st.effectiveAction === "blocked" && !isDup && blockCodes.length === 0;

  // Nothing to fix -> render nothing. The single header status line already
  // says the item is ready; a second green "ready" box here is just noise.
  if (!showDup && blockCodes.length === 0 && !otherBlock) {
    return null;
  }

  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
      <p className="text-xs font-bold uppercase tracking-wide text-red-800">
        Fix before writing
      </p>
      <ul className="mt-2 space-y-1.5">
        {showDup && st.dupMatch && (
          <li className="text-sm leading-snug text-red-700">
            <span className="font-semibold">⛔ Duplicate</span> — already in the
            catalog as{" "}
            <button
              type="button"
              onClick={() => st.dupMatch && onOpenMatch(st.dupMatch)}
              className="font-medium underline decoration-dotted hover:text-red-900"
            >
              {st.dupMatch.name}
              {st.dupMatch.brand ? ` (${st.dupMatch.brand})` : ""}
            </button>
            . Merge, overwrite, or skip below.
          </li>
        )}
        {blockCodes.map((code) => {
          const v = describeConcern(code);
          const why = explainConcern(code, proposed);
          return (
            <li key={code} className="text-sm leading-snug text-red-700">
              <span className="font-semibold">⛔ {v.name}</span>
              {why ? <span className="text-red-700/90"> — {why}</span> : null}
            </li>
          );
        })}
        {otherBlock &&
          (reasons && reasons.length > 0
            ? reasons
            : ["Can't be written yet."]
          ).map((r, ri) => (
            <li key={ri} className="text-sm leading-snug text-red-700">
              <span className="font-semibold">⛔</span>{" "}
              <span className="text-red-700/90">{r}</span>
            </li>
          ))}
      </ul>
    </div>
  );
};

// A filled, colored action button (used for the AI-merge actions so they stand
// out from the neutral secondary buttons).
const AccentButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    color?: "violet" | "green" | "sky";
  }
> = ({ className = "", color = "violet", children, ...rest }) => {
  const tones = {
    violet: "bg-violet-600 hover:bg-violet-700",
    green: "bg-green-600 hover:bg-green-700",
    sky: "bg-sky-600 hover:bg-sky-700",
  };
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50 ${tones[color]} ${className}`}
    >
      {children}
    </button>
  );
};

type LensView = "existing" | "new" | "merged";

/** Existing / New / Merged segmented toggle for a duplicate's food view. */
const LensTabs: React.FC<{
  view: LensView;
  hasMerged: boolean;
  onView: (v: LensView) => void;
  size?: "sm" | "md";
}> = ({ view, hasMerged, onView, size = "md" }) => {
  const activeView: LensView = view === "merged" && !hasMerged ? "new" : view;
  const tabs: { id: LensView; label: string; show: boolean }[] = [
    { id: "existing", label: "Existing", show: true },
    { id: "new", label: "New", show: true },
    { id: "merged", label: "✓ Merged", show: hasMerged },
  ];
  const pad = size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm";
  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-gray-100 p-0.5">
      {tabs
        .filter((t) => t.show)
        .map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView(t.id);
            }}
            className={`rounded-md font-semibold transition ${pad} ${
              activeView === t.id
                ? t.id === "merged"
                  ? "bg-green-600 text-white shadow"
                  : "bg-white text-gray-900 shadow"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
    </div>
  );
};

/**
 * One product, three lenses. A segmented control flips a SINGLE food view
 * between the Existing record, the New (proposed) data, and the AI-Merged
 * union — instead of stacking multiple food cards. The field-by-field decision
 * list shows exactly what the merge reconciled.
 */
const MergeStudio: React.FC<{
  dupMatch: ExistingMatch;
  existing?: PassioFood | null;
  loadingExisting?: boolean;
  proposed: PassioFood;
  mergeResult?: MergeResult;
  mergeRunning?: boolean;
  mergeError?: string;
  mergeApplied?: boolean;
  /** Concern codes re-computed on the merged result (fresh sanity checks). */
  mergeConcerns?: number[];
  /** Whether the merged result carries a blocking concern. */
  mergeBlocking?: boolean;
  mergeChainId: string;
  view: "existing" | "new" | "merged";
  editing?: boolean;
  rechecking?: boolean;
  ingesting?: boolean;
  /** Prominent "Linked labels" control, shown beneath the food view. */
  labelSlot?: React.ReactNode;
  onView: (v: "existing" | "new" | "merged") => void;
  onOpenExisting: () => void;
  onChangeProposed: (f: PassioFood) => void;
  onEditingChange: (b: boolean) => void;
  onRecheck: () => void;
  onMerge: () => void;
  onOverwrite: () => void;
  onSkip: () => void;
  onApply: () => void;
}> = ({
  dupMatch,
  existing,
  loadingExisting,
  proposed,
  mergeResult,
  mergeRunning,
  mergeError,
  mergeApplied,
  mergeConcerns,
  mergeBlocking,
  mergeChainId,
  view,
  editing,
  rechecking,
  ingesting,
  labelSlot,
  onView,
  onOpenExisting,
  onChangeProposed,
  onEditingChange,
  onRecheck,
  onMerge,
  onOverwrite,
  onSkip,
  onApply,
}) => {
  const hasMerged = !!mergeResult?.merged;
  // If merged isn't available yet but the tab is selected, fall back to new.
  const activeView = view === "merged" && !hasMerged ? "new" : view;
  const viewingMerged = activeView === "merged" && hasMerged;
  // The merge kept everything from existing — writing it would change nothing.
  const noChange = hasMerged && mergeIsNoOp(existing, mergeResult?.merged);
  // Concerns re-computed on the merged output — a blocking one stops the write.
  const mergedBlockCodes = blockingConcerns(mergeConcerns);
  const mergedBlocked = !!mergeBlocking || mergedBlockCodes.length > 0;

  return (
    <div
      className={`rounded-md border bg-white ${
        viewingMerged
          ? mergedBlocked
            ? "border-red-200"
            : noChange
            ? "border-sky-200"
            : "border-green-200"
          : "border-red-200"
      }`}
    >
      {/* Context banner — green once you're looking at the merged result;
          sky/informational when the merge turned out to be a no-op; red when
          the re-checked merge still trips a blocking concern. */}
      {viewingMerged && mergedBlocked ? (
        <div className="border-b border-red-100 bg-red-50/70 px-3 py-2.5">
          <p className="text-sm font-semibold text-red-800">
            ⛔ Merged result has blocking concerns — fix before writing
          </p>
          <div className="mt-1 text-sm text-red-700">
            We re-ran the sanity checks on the merged record and it still has
            issues. Switch to the <span className="font-semibold">New</span>{" "}
            lens to edit and Re-check, then merge again.
          </div>
        </div>
      ) : viewingMerged && noChange ? (
        <div className="border-b border-sky-100 bg-sky-50/70 px-3 py-2.5">
          <p className="text-sm font-semibold text-sky-800">
            ✓ No changes needed — already up to date
          </p>
          <div className="mt-1 text-sm text-sky-700">
            The merge kept everything from the existing record for{" "}
            <button
              type="button"
              onClick={onOpenExisting}
              className="font-medium underline decoration-dotted hover:text-sky-900"
            >
              {dupMatch.name}
              {dupMatch.brand ? ` (${dupMatch.brand})` : ""}
            </button>
            , so the researched data adds nothing new. Writing it would have no
            effect — you can safely <span className="font-semibold">skip</span>{" "}
            this one.
          </div>
        </div>
      ) : viewingMerged ? (
        <div className="border-b border-green-100 bg-green-50/70 px-3 py-2.5">
          <p className="text-sm font-semibold text-green-800">
            ✓ Merged result — this is what will be written
          </p>
          <div className="mt-1 text-sm text-green-700">
            Applied as an update to{" "}
            <button
              type="button"
              onClick={onOpenExisting}
              className="font-medium underline decoration-dotted hover:text-green-900"
            >
              {dupMatch.name}
              {dupMatch.brand ? ` (${dupMatch.brand})` : ""}
            </button>
            . Review it, then Apply below.
          </div>
        </div>
      ) : (
        <div className="border-b border-red-100 bg-red-50/60 px-3 py-2.5">
          <p className="text-sm font-semibold text-red-800">
            ⛔ Already in the catalog — resolve this duplicate
          </p>
          <div className="mt-1 text-sm text-red-700">
            Matches{" "}
            <button
              type="button"
              onClick={onOpenExisting}
              className="font-medium underline decoration-dotted hover:text-red-900"
            >
              {dupMatch.name}
              {dupMatch.brand ? ` (${dupMatch.brand})` : ""}
            </button>
            {dupMatch.product_code && (
              <span className="ml-2 font-mono text-red-600">
                UPC {dupMatch.product_code}
              </span>
            )}
            . Compare the versions, let AI merge them, overwrite, or skip.
          </div>
        </div>
      )}

      {/* Active-lens caption (the Existing/New/Merged toggle lives in the
          sticky header so you can flip it while scrolled down diffing) */}
      <div className="flex items-center gap-2 px-3 pt-3">
        <LensTabs view={view} hasMerged={hasMerged} onView={onView} />
        <span className="text-xs text-gray-400">
          {activeView === "existing"
            ? "what's in the catalog now"
            : activeView === "new"
            ? "the researched data (editable)"
            : "the union that will be written"}
        </span>
      </div>

      {/* Single food view for the active lens */}
      <div className="p-3">
        {/* Each lens gets its own key so the panel remounts on switch and
            recomputes its portion/quantity defaults for that record (otherwise
            the qty/portion selection leaks across foods and the math breaks). */}
        {activeView === "existing" ? (
          loadingExisting ? (
            <Spinner label="loading existing record…" />
          ) : existing ? (
            <FoodDetailPanel key="existing" food={existing} hideConcernList />
          ) : (
            <p className="text-sm text-gray-500">
              Couldn&apos;t load the existing record.
            </p>
          )
        ) : activeView === "merged" && mergeResult?.merged ? (
          <FoodDetailPanel
            key="merged"
            food={mergeResult.merged}
            hideConcernList
          />
        ) : (
          <FoodDetailPanel
            key="new"
            food={proposed}
            onChange={onChangeProposed}
            editing={editing}
            onEditingChange={onEditingChange}
            onRecheck={onRecheck}
            rechecking={rechecking}
            hideEditToggle
            hideConcernList
          />
        )}
        {/* Blocking concerns re-computed on the merged output — surfaced here so
            a merge can't be applied/written while it still fails a check. */}
        {viewingMerged && mergedBlockCodes.length > 0 && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-xs font-bold uppercase tracking-wide text-red-800">
              Fix before writing (checked on the merged result)
            </p>
            <ul className="mt-2 space-y-1.5">
              {mergedBlockCodes.map((code) => {
                const v = describeConcern(code);
                const why = explainConcern(code, mergeResult?.merged);
                return (
                  <li key={code} className="text-sm leading-snug text-red-700">
                    <span className="font-semibold">⛔ {v.name}</span>
                    {why ? (
                      <span className="text-red-700/90"> — {why}</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {/* Linked labels apply to the existing catalog record this merges into,
            so show the control once beneath whichever lens is active. */}
        {labelSlot && <div className="mt-4">{labelSlot}</div>}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-3 py-2.5">
        {!mergeResult && (
          <AccentButton
            type="button"
            onClick={onMerge}
            disabled={mergeRunning || !mergeChainId}
          >
            {mergeRunning ? "Merging…" : "✨ AI compare & merge"}
          </AccentButton>
        )}
        {hasMerged && !mergeApplied && noChange && (
          <AccentButton type="button" color="sky" onClick={onSkip}>
            Keep existing (nothing to write)
          </AccentButton>
        )}
        {hasMerged && !mergeApplied && !noChange && (
          <AccentButton
            type="button"
            color="green"
            onClick={onApply}
            disabled={
              mergeResult?.recommendation === "needs_review" || mergedBlocked
            }
          >
            {mergedBlocked
              ? "Blocked — fix concerns first"
              : "Apply merged & select for update"}
          </AccentButton>
        )}
        <SecondaryButton
          type="button"
          onClick={onOverwrite}
          disabled={ingesting}
        >
          Overwrite existing with New
        </SecondaryButton>
        {!(hasMerged && !mergeApplied && noChange) && (
          <button
            type="button"
            onClick={onSkip}
            className="text-sm font-medium text-gray-500 underline decoration-dotted hover:text-gray-700"
          >
            Keep existing (skip)
          </button>
        )}
        {mergeRunning && <Spinner label="running merge chain" />}
        {!mergeChainId && (
          <span className="text-sm text-amber-700">
            No merge chain id configured.
          </span>
        )}
      </div>

      {hasMerged && !mergeApplied && !noChange && (
        <div className="border-t border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Heads up: <span className="font-semibold">Apply</span> just stages
          this merge as an update and moves it to{" "}
          <span className="font-semibold">Ready to write</span> — it is
          <span className="font-semibold"> not saved</span> until you click{" "}
          <span className="font-semibold">Write to catalog</span>.
        </div>
      )}

      {mergeError && (
        <div className="px-3 pb-3 text-sm text-red-600">{mergeError}</div>
      )}

      {/* Merge outcome: recommendation + field decision list */}
      {mergeResult && (
        <div className="border-t border-gray-100 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-gray-800">AI merge:</span>
            <Badge
              tone={
                mergeResult.recommendation === "merge"
                  ? "green"
                  : mergeResult.recommendation === "needs_review"
                  ? "amber"
                  : "gray"
              }
            >
              {mergeResult.recommendation}
            </Badge>
            {typeof mergeResult.confidence === "number" && (
              <span className="text-gray-500">
                confidence {Math.round(mergeResult.confidence * 100)}%
              </span>
            )}
            {noChange && <Badge tone="blue">no change vs. existing</Badge>}
            {mergeResult.recommendation === "needs_review" && (
              <span className="text-amber-700">
                — records conflict; review before applying.
              </span>
            )}
          </div>
          {mergeResult.field_log && mergeResult.field_log.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                What the merge decided ({mergeResult.field_log.length} fields)
              </p>
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-md border border-gray-100">
                {mergeResult.field_log.map((f, fi) => (
                  <li
                    key={fi}
                    className="flex flex-wrap items-baseline gap-x-2 px-2.5 py-1.5 text-sm"
                  >
                    <span className="rounded bg-indigo-50 px-1.5 py-0.5 font-mono text-xs font-semibold text-indigo-700">
                      {f.field}
                    </span>
                    <span className="font-medium text-gray-800">
                      {f.chosen}
                    </span>
                    {f.reason && (
                      <span className="text-gray-500">— {f.reason}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <JsonBlock value={mergeResult.merged} label="merged — raw JSON" />
        </div>
      )}
    </div>
  );
};

const ReviewTab: React.FC<ReviewTabProps> = ({
  api,
  mergeChainId,
  reviewItems,
  executionId,
  customerName,
  autoMerge = true,
  onItemsWritten,
  writtenKeys,
  retry,
}) => {
  const [states, setStates] = useState<Record<string, ItemState>>({});
  // Items swapped in by a per-item retry, keyed by review-item key. Rendered in
  // place of the original so a retried row shows fresh data WITHOUT reseeding the
  // whole queue (which would drop other rows' selections/edits/merges).
  const [itemOverrides, setItemOverrides] = useState<
    Record<string, IngestReviewItem>
  >({});
  const [ingesting, setIngesting] = useState(false);
  const [merging, setMerging] = useState(false);
  const [banner, setBanner] = useState<string>("");
  const [toast, setToast] = useState<{
    message: string;
    tone?: "info" | "success" | "warn";
    write?: boolean;
  } | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<
    Partial<Record<GroupId, boolean>>
  >({});

  // Reusable food-detail drawer (existing-match previews).
  const [drawer, setDrawer] = useState<{
    open: boolean;
    loading: boolean;
    error?: string;
    title?: string;
    subtitle?: string;
    food?: PassioFood | null;
  }>({ open: false, loading: false });

  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));

  const openMatch = async (m: ExistingMatch) => {
    const label = [m.name, m.brand ? `(${m.brand})` : ""]
      .filter(Boolean)
      .join(" ");
    if (!m.ref_code && !m.id) {
      setDrawer({
        open: true,
        loading: false,
        title: label,
        subtitle: "Existing catalog record",
        error: "This match has no ref code, so its details can't be loaded.",
      });
      return;
    }
    setDrawer({
      open: true,
      loading: true,
      title: label,
      subtitle: "Existing catalog record",
    });
    try {
      const food = m.ref_code
        ? await api.getFoodByRefCode(m.ref_code)
        : await api.getFoodById(m.id as string);
      setDrawer({
        open: true,
        loading: false,
        title: label,
        subtitle: "Existing catalog record",
        food,
        error: food ? undefined : "No details found for this match.",
      });
    } catch (err) {
      setDrawer({
        open: true,
        loading: false,
        title: label,
        subtitle: "Existing catalog record",
        error: extractApiError(err, "Failed to load match details"),
      });
    }
  };

  // Tracks duplicate keys we've already auto-kicked a merge for, so the
  // auto-merge effect fires exactly once per item (even as state updates).
  const autoMergeStarted = useRef<Set<string>>(new Set());

  // Keys already written on a prior visit to this run (persisted on the ingest
  // job). Read via a ref so the init effect can seed them WITHOUT re-running
  // (and clobbering live state) every time a write updates the set.
  const writtenKeysSet = useMemo(
    () => new Set(writtenKeys || []),
    [writtenKeys]
  );
  const writtenKeysRef = useRef(writtenKeysSet);
  writtenKeysRef.current = writtenKeysSet;

  // Build the per-item review state for one item. Shared by the initial seeding
  // effect and by the per-item retry (which rebuilds only the retried row).
  const buildItemState = React.useCallback(
    (item: IngestReviewItem, key: string): ItemState => {
      const action =
        (item.decision?.action as ItemState["effectiveAction"]) || "create";
      const payload = (item.ingest_payload ||
        item.proposed ||
        {}) as PassioFood;
      // A shared UPC (padding-insensitive) with an existing record is a hard
      // duplicate regardless of what the decision said — surface it and require
      // a choice (merge / overwrite / skip) rather than blind-creating.
      const dupMatch = findUpcDuplicate(item, payload);
      const upcConflict = !!dupMatch;
      // Was this item already written on a previous visit to this run? If so,
      // seed it as written (green group, not selectable) so a tab switch or
      // reload doesn't resurrect it as still-to-write.
      const alreadyWritten = writtenKeysRef.current.has(key);
      return {
        selected: false,
        effectiveAction: upcConflict ? "blocked" : action,
        effectivePayload: payload,
        targetId: dupMatch?.id ?? item.decision?.target_id ?? null,
        upcConflict,
        dupMatch,
        collapsed: alreadyWritten,
        editing: false,
        // Seed the linked labels from the labels the ingest AI SELECTED
        // (label_links), falling back to the legacy single label_link. The
        // remaining retrieved leaf labels (label_options) become one-tap
        // "Options" chips. All editable via LabelLink.
        chosenLabels: dedupeLinks(
          (
            (item.label_links && item.label_links.length
              ? item.label_links
              : item.label_link
              ? [item.label_link]
              : []) as IngestLabelRef[]
          )
            .map(refToLink)
            .filter((l) => l.labelId && l.displayName)
        ),
        labelSuggestions: (item.label_options || [])
          .map(refToMatch)
          .filter((m) => m.labelId && m.displayName),
        ...(alreadyWritten ? { outcome: { status: "ok" as const } } : {}),
      };
    },
    []
  );

  // Build initial per-item state whenever the incoming items change.
  useEffect(() => {
    autoMergeStarted.current = new Set();
    setItemOverrides({});
    const next: Record<string, ItemState> = {};
    reviewItems.forEach((item, i) => {
      const key = keyFor(item, i);
      next[key] = buildItemState(item, key);
    });
    setStates(next);
  }, [reviewItems, buildItemState]);

  // Label selection now comes from the ingest chain itself: the format AI picks
  // the fitting labels (seeded into chosenLabels above) from the wide-net leaf
  // options it was given (seeded into labelSuggestions above, shown as one-tap
  // "Options" chips). There is no separate string-similarity auto-match here —
  // it linked nonsense (shared-token collisions like "prawn crackers").

  const patch = (key: string, p: Partial<ItemState>) =>
    setStates((s) => ({ ...s, [key]: { ...s[key], ...p } }));

  const selectableKeys = useMemo(
    () =>
      reviewItems
        .map((item, i) => keyFor(item, i))
        .filter((k) => {
          const st = states[k];
          return (
            st &&
            !st.discarded &&
            (st.effectiveAction === "create" ||
              st.effectiveAction === "update") &&
            !st.upcConflict
          );
        }),
    [reviewItems, states]
  );

  const selectedCount = selectableKeys.filter(
    (k) => states[k]?.selected
  ).length;

  // Bucket every item into a group so we can render tidy, collapsible sections
  // and offer per-group bulk actions.
  const grouped = useMemo(() => {
    const g: Record<
      GroupId,
      { item: IngestReviewItem; key: string; i: number }[]
    > = {
      ready: [],
      duplicate: [],
      attention: [],
      written: [],
      discarded: [],
    };
    reviewItems.forEach((item, i) => {
      const key = keyFor(item, i);
      const st = states[key];
      if (!st) return;
      // Render the retried item's fresh data (if any) in place of the original.
      g[groupOf(st)].push({ item: itemOverrides[key] || item, key, i });
    });
    return g;
  }, [reviewItems, states, itemOverrides]);

  // ----- per-item retry (single-item background re-run) ---------------------
  // Kick off a fresh ingest run for just this item (to try again for missing
  // info like nutrition). The row is locked while it runs; on completion the
  // fresh result is swapped in for this row only.
  const startItemRetry = (key: string, i: number, item: IngestReviewItem) => {
    if (!retry) return;
    const st = states[key];
    if (st?.retrying) return;
    const payload = st?.effectivePayload || ({} as PassioFood);
    const name = String(payload.name || item.name || "").trim();
    const brand = String(
      payload.brandName || payload.branded?.owner || item.brand || ""
    ).trim();
    const foodInput = (
      item.original_input ||
      [brand, name].filter(Boolean).join(" ") ||
      name
    ).trim();
    if (!foodInput) {
      patch(key, { retryError: "Nothing to search for (no name/brand)." });
      return;
    }
    patch(key, { retrying: true, retryError: undefined });
    retry
      .start(foodInput, name || foodInput, i)
      .then((jobId) => patch(key, { retryJobId: jobId }))
      .catch((err) =>
        patch(key, {
          retrying: false,
          retryError: extractApiError(err, "Couldn't start retry."),
        })
      );
  };

  // Retry jobs we've already consumed, so a re-render before state settles can't
  // apply the same result (or toast) twice.
  const handledRetryJobsRef = useRef<Set<string>>(new Set());

  // Watch in-flight retries and apply/clean up when they finish.
  useEffect(() => {
    if (!retry) return;
    Object.entries(states).forEach(([key, st]) => {
      if (!st.retrying || !st.retryJobId) return;
      const job = retry.jobs.find((j) => j.id === st.retryJobId);
      if (!job) return;
      if (handledRetryJobsRef.current.has(job.id)) return;
      if (job.status === "completed") {
        handledRetryJobsRef.current.add(job.id);
        const fresh = job.resultItems && job.resultItems[0];
        const idx = reviewItems.findIndex((it, k) => keyFor(it, k) === key);
        if (fresh && idx >= 0) {
          // Keep the row's key stable so state/overrides stay aligned.
          const withKey = { ...fresh, __key: key } as IngestReviewItem;
          setItemOverrides((o) => ({ ...o, [key]: withKey }));
          setStates((s) => ({ ...s, [key]: buildItemState(withKey, key) }));
          retry.persist(idx, withKey);
          setToast({
            message: "Re-ran the item with fresh research.",
            tone: "success",
          });
        } else {
          patch(key, {
            retrying: false,
            retryError: "Retry returned no result.",
          });
        }
        retry.clear(job.id);
      } else if (job.status === "failed" || job.status === "cancelled") {
        handledRetryJobsRef.current.add(job.id);
        patch(key, {
          retrying: false,
          retryError: job.error || "Retry didn't finish.",
        });
        retry.clear(job.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry?.jobs, states]);

  const jumpToIndex = (i: number) => {
    const el = document.getElementById(`review-item-${i}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const dupKeys = grouped.duplicate.map((x) => x.key);
  const selectedDupKeys = dupKeys.filter((k) => states[k]?.selected);
  const mergeableSelectedDups = selectedDupKeys.filter(
    (k) => !states[k]?.mergeResult
  );
  const applicableMerges = dupKeys.filter((k) => {
    const st = states[k];
    return (
      st?.mergeResult?.merged &&
      !st.mergeApplied &&
      !st.mergeBlocking &&
      st.mergeResult.recommendation !== "needs_review"
    );
  });

  // Headline breakdown so the user instantly sees the state of the batch.
  const summary = useMemo(() => {
    let ready = 0;
    let duplicates = 0;
    let attention = 0;
    let written = 0;
    reviewItems.forEach((item, i) => {
      const st = states[keyFor(item, i)];
      if (!st) return;
      if (st.outcome?.status === "ok") written++;
      else if (st.upcConflict) duplicates++;
      else if (st.effectiveAction === "blocked") attention++;
      else ready++;
    });
    return { ready, duplicates, attention, written };
  }, [reviewItems, states]);

  // ----- inline editing (edits the proposed payload in place via the panel) -----

  const changePayload = (key: string, next: PassioFood) =>
    setStates((s) => ({
      ...s,
      [key]: {
        ...s[key],
        effectivePayload: next,
        edited: true,
        pendingRecheck: true,
      },
    }));

  // Called when the inline editor is closed ("Done editing"). If anything was
  // changed since the last re-check, automatically re-run the sanity checks so
  // the block/allow state always reflects the latest edits.
  const handleEditingChange = (key: string, isEditing: boolean) => {
    if (!isEditing && states[key]?.pendingRecheck && !states[key]?.rechecking) {
      void recheck(key);
    }
  };

  // Sticky-header owns the Edit/Done toggle; flip the controlled editing flag
  // and run the same close-time auto re-check. For a duplicate, editing acts on
  // the "New" data, so make sure that lens is showing.
  const setItemEditing = (key: string, next: boolean) => {
    patch(key, next ? { editing: true, mergeView: "new" } : { editing: false });
    handleEditingChange(key, next);
  };

  // Switch the duplicate studio lens; lazily fetch the existing record the
  // first time the "Existing" lens is opened.
  const setMergeView = (key: string, v: "existing" | "new" | "merged") => {
    patch(key, { mergeView: v });
    if (v === "existing") void loadExisting(key);
  };

  // Re-run the hardcoded sanity checks server-side and re-apply concern codes,
  // then re-derive whether the (edited) item can be ingested.
  const recheck = async (key: string) => {
    const st = states[key];
    if (!st) return;
    patch(key, { rechecking: true, recheckMsg: "" });
    try {
      const res = await api.recheckConcerns(st.effectivePayload);
      const upc = upcOf(res.food).trim();
      let upcConflict = false;
      let targetId = st.targetId;
      let dupMatch = st.dupMatch ?? null;
      if (res.decision?.action === "blocked") upcConflict = false; // concern-block, not a dup
      if (upc) {
        try {
          const existing = await api.getFoodByUpc(upc);
          if (existing?.id && existing.id !== st.targetId) {
            upcConflict = true;
            targetId = existing.id;
            dupMatch = matchFromFood(existing);
          }
        } catch {
          /* lookup failure shouldn't block prepping */
        }
      }
      // The backend decision/blocking is authoritative (its block set is wider
      // than client severity-1); fall back to the client heuristic if absent.
      let concernBlocking: boolean;
      if (typeof res.blocking === "boolean") concernBlocking = res.blocking;
      else if (res.decision?.action)
        concernBlocking = res.decision.action === "blocked";
      else concernBlocking = hasBlockingConcern(res.concern_codes);
      const blocking = concernBlocking || upcConflict;
      setStates((s) => {
        const cur = s[key];
        if (!cur) return s;
        return {
          ...s,
          [key]: {
            ...cur,
            effectivePayload: res.food,
            rechecking: false,
            pendingRecheck: false,
            upcConflict,
            dupMatch,
            targetId,
            effectiveAction: blocking
              ? "blocked"
              : cur.effectiveAction === "blocked"
              ? "create"
              : cur.effectiveAction,
            selected: blocking ? false : cur.selected,
            recheckMsg: blocking
              ? upcConflict
                ? "Duplicate UPC — must merge with the existing record."
                : "Still blocked by concern codes."
              : "",
          },
        };
      });
    } catch (err) {
      patch(key, {
        rechecking: false,
        pendingRecheck: false,
        recheckMsg: extractApiError(err, "Re-check failed"),
      });
    }
  };

  const runMerge = async (item: IngestReviewItem, key: string) => {
    patch(key, { mergeRunning: true, mergeError: undefined });
    try {
      const candidate = (states[key]?.effectivePayload ||
        item.ingest_payload ||
        item.proposed ||
        {}) as PassioFood;
      const upc = upcOf(candidate);
      const existing = upc ? await api.getFoodByUpc(upc) : null;
      if (!existing) {
        throw new Error(
          "Could not load the existing record for this UPC to merge against."
        );
      }
      const resp = await api.runChain(
        mergeChainId,
        { pairs: [{ candidate, existing }], do_web_research: true },
        { metadata: { feature: "merge" } }
      );
      const results = extractMergeResults(resp);
      const result = results[0];
      if (!result?.merged) throw new Error("Merge chain returned no result.");

      // The merge chain produced NEW data by reconciling the candidate and the
      // existing record, so its concern codes are stale. Re-run the hardcoded
      // sanity checks on the merged output before we show it (and long before
      // it can be written) — same guarantee we give edited/created items.
      let mergedFood = result.merged;
      let mergeConcerns = Array.isArray(mergedFood.concerns)
        ? mergedFood.concerns
        : [];
      let mergeBlocking = hasBlockingConcern(mergeConcerns);
      try {
        const rc = await api.recheckConcerns(mergedFood);
        mergeConcerns = Array.isArray(rc.concern_codes)
          ? rc.concern_codes
          : mergeConcerns;
        // Prefer the backend's authoritative decision/blocking flag.
        if (typeof rc.blocking === "boolean") mergeBlocking = rc.blocking;
        else if (rc.decision?.action)
          mergeBlocking = rc.decision.action === "blocked";
        else mergeBlocking = hasBlockingConcern(mergeConcerns);
        // Use the normalized/repaired record (keeping its id) with the fresh
        // concern codes so the merged view reflects exactly what will be saved.
        mergedFood = {
          ...rc.food,
          id: mergedFood.id ?? rc.food.id,
          concerns: mergeConcerns,
        };
      } catch {
        // Re-check failed — fall back to whatever concerns rode along on the
        // merged record so we still surface (rather than hide) any problems.
        mergedFood = { ...mergedFood, concerns: mergeConcerns };
      }

      patch(key, {
        mergeRunning: false,
        mergeResult: { ...result, merged: mergedFood },
        mergeConcerns,
        mergeBlocking,
        mergeExisting: existing,
        mergeView: "merged",
      });
    } catch (err) {
      patch(key, {
        mergeRunning: false,
        mergeError: extractApiError(err, "Merge failed"),
      });
    }
  };

  // Auto-kick the AI merge for every duplicate as soon as the page has state,
  // so the merged diff is ready without the user clicking into each one. The
  // ref guard ensures we start each exactly once (state updates re-run this).
  useEffect(() => {
    if (!autoMerge || !mergeChainId) return;
    grouped.duplicate.forEach(({ item, key }) => {
      const st = states[key];
      if (!st) return;
      if (st.mergeResult || st.mergeRunning || st.mergeError) return;
      if (autoMergeStarted.current.has(key)) return;
      autoMergeStarted.current.add(key);
      runMerge(item, key);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMerge, mergeChainId, grouped.duplicate, states]);

  // Fetch the full existing record so we can render a side-by-side comparison
  // (independent of running the merge chain).
  const loadExisting = async (key: string) => {
    const st = states[key];
    if (!st?.dupMatch || st.mergeExisting || st.loadingExisting) return;
    patch(key, { loadingExisting: true });
    try {
      const m = st.dupMatch;
      const upc = upcOf(st.effectivePayload).trim();
      const food = m.ref_code
        ? await api.getFoodByRefCode(m.ref_code)
        : m.id
        ? await api.getFoodById(m.id)
        : upc
        ? await api.getFoodByUpc(upc)
        : null;
      patch(key, { mergeExisting: food ?? null, loadingExisting: false });
    } catch {
      patch(key, { loadingExisting: false });
    }
  };

  // ----- bulk duplicate actions -----

  // Run the merge chain for every selected duplicate that hasn't been merged.
  const mergeSelectedDuplicates = async () => {
    setMerging(true);
    for (const { item, key } of grouped.duplicate) {
      if (states[key]?.selected && !states[key]?.mergeResult) {
        // eslint-disable-next-line no-await-in-loop
        await runMerge(item, key);
      }
    }
    setMerging(false);
  };

  // Apply every safe (non "needs_review") merge result and queue it for update.
  const applyAllMerges = () => {
    const n = applicableMerges.length;
    applicableMerges.forEach((key) => applyMerge(key));
    if (n > 0) {
      setBanner(
        `Applied ${n} merge${
          n === 1 ? "" : "s"
        } and moved to “Ready to write”, selected. Nothing is saved yet — click “Write … selected to catalog” to save.`
      );
      setToast({
        message: `${n} merge${
          n === 1 ? "" : "s"
        } staged — nothing is saved yet. You still need to Write ${
          n === 1 ? "it" : "them"
        } to the catalog.`,
        tone: "warn",
        write: true,
      });
    }
  };

  const setAllCollapsed = (collapsed: boolean) =>
    setStates((s) => {
      const n = { ...s };
      Object.keys(n).forEach((k) => {
        n[k] = { ...n[k], collapsed };
      });
      return n;
    });

  // Toggle selection for a whole group at once.
  const toggleGroupSelect = (keys: string[]) =>
    setStates((s) => {
      const all = keys.length > 0 && keys.every((k) => s[k]?.selected);
      const n = { ...s };
      keys.forEach((k) => {
        if (n[k]) n[k] = { ...n[k], selected: !all };
      });
      return n;
    });

  const applyMerge = (key: string) => {
    const st = states[key];
    const r = st?.mergeResult;
    if (!r?.merged) return;
    // A merged result that still trips the sanity checks must NOT be staged for
    // write — the same rule we enforce for edited/created items. Route it to
    // "attention" instead so the user fixes the flagged fields first.
    if (st.mergeBlocking) {
      patch(key, {
        effectiveAction: "blocked",
        effectivePayload: r.merged,
        targetId: r.merged.id ?? st.targetId,
        upcConflict: false,
        mergeApplied: false,
        selected: false,
        mergeView: "merged",
        recheckMsg:
          "The merged record still has blocking concern codes. Fix them on the New lens (edit, then Re-check) before it can be written.",
      });
      setToast({
        message:
          "Can't apply this merge — the merged record has blocking concern codes. Resolve them first.",
        tone: "warn",
      });
      return;
    }
    patch(key, {
      effectiveAction: "update",
      effectivePayload: r.merged,
      targetId: r.merged.id ?? st.targetId,
      upcConflict: false,
      mergeApplied: true,
      selected: true,
    });
    setBanner(
      "Merge applied and moved to “Ready to write”, selected for you. Nothing is saved yet — click “Write … selected to catalog” to save it."
    );
    setToast({
      message:
        "Merge staged — nothing is saved yet. You still need to Write it to the catalog.",
      tone: "warn",
      write: true,
    });
  };

  // Manual duplicate resolution: overwrite the existing record with this data
  // (writes as an UPDATE to the matched id — no data-loss merge, user's call).
  const overwriteExisting = (key: string) => {
    const st = states[key];
    if (!st?.dupMatch?.id) return;
    patch(key, {
      effectiveAction: "update",
      targetId: st.dupMatch.id,
      upcConflict: false,
      selected: true,
    });
  };

  // Look up the candidate label phrases the ingest proposed for an item (echoed
  // in the completion email when nothing was linked).
  const labelCandidatesFor = (key: string): string[] | undefined => {
    const idx = reviewItems.findIndex((it, i) => keyFor(it, i) === key);
    const cands = idx >= 0 ? reviewItems[idx]?.candidate_labels : undefined;
    return Array.isArray(cands) && cands.length > 0 ? cands : undefined;
  };

  // Best-effort: link every chosen label to a just-written food (which now has
  // an id). Returns a comma-joined list of linked label names (for the ops
  // email), or "". Never throws — the write already succeeded.
  const linkChosenLabels = async (
    chosen: FoodLabelLink[] | undefined,
    foodId?: string
  ): Promise<string> => {
    if (!chosen?.length || !foodId) return "";
    const linked: string[] = [];
    for (const c of chosen) {
      if (!c?.labelId) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.linkFoodLabel({ labelId: c.labelId, foodId });
        if (c.displayName) linked.push(c.displayName);
      } catch {
        // Non-fatal: skip this one; the email flags whatever didn't link.
      }
    }
    return linked.join(", ");
  };

  const ingestSelected = async (keysArg?: string[]) => {
    setBanner("");
    setIngesting(true);
    const keys = (
      keysArg ?? selectableKeys.filter((k) => states[k]?.selected)
    ).filter((k) => {
      const st = states[k];
      return (
        st &&
        st.outcome?.status !== "ok" &&
        (st.effectiveAction === "create" || st.effectiveAction === "update") &&
        !st.upcConflict
      );
    });
    let ok = 0;
    let failed = 0;
    let skipped = 0;
    const written: { key: string; id?: string }[] = [];
    for (const key of keys) {
      const st = states[key];
      if (!st) continue;
      try {
        if (st.effectiveAction === "create") {
          // Last-line duplicate guard: a UPC discovered during research may
          // already exist even if nothing flagged it earlier. Check before we
          // create so we route to merge instead of hitting a 412.
          const upc = upcOf(st.effectivePayload).trim();
          if (upc) {
            const existing = await api.getFoodByUpc(upc).catch(() => null);
            if (existing?.id) {
              patch(key, {
                effectiveAction: "blocked",
                upcConflict: true,
                dupMatch: matchFromFood(existing),
                targetId: existing.id,
                selected: false,
                recheckMsg:
                  "Already in the catalog (same UPC) — resolve as a duplicate below.",
              });
              skipped++;
              continue;
            }
          }
          const created = await api.createFood(
            st.effectivePayload,
            executionId
          );
          // Auto-link the chosen/suggested labels so the new item surfaces in
          // search. Best-effort: never fail the write on a link error.
          const linkedLabel = await linkChosenLabels(
            st.chosenLabels,
            created.id
          );
          await api.notifyChange({
            action: "create",
            food: created,
            customerName,
            linkedLabel,
            candidateLabels: labelCandidatesFor(key),
          });
          patch(key, {
            outcome: { status: "ok", id: created.id },
            selected: false,
            collapsed: true,
          });
          written.push({ key, id: created.id });
          ok++;
        } else {
          const id = st.targetId || st.effectivePayload.id;
          if (!id) throw new Error("Missing target id for update");
          const original = await api.getFoodById(id).catch(() => null);
          const toWrite: PassioFood = { ...st.effectivePayload, id };
          const updated = await api.updateFood(toWrite, executionId);
          const linkedLabel = await linkChosenLabels(st.chosenLabels, id);
          await api.notifyChange({
            action: "update",
            food: updated,
            original,
            customerName,
            linkedLabel,
            candidateLabels: labelCandidatesFor(key),
          });
          patch(key, {
            outcome: { status: "ok", id },
            selected: false,
            collapsed: true,
          });
          written.push({ key, id });
          ok++;
        }
      } catch (err) {
        const dup = isDuplicateUpcError(err);
        patch(key, {
          outcome: {
            status: dup ? "blocked" : "failed",
            error: dup
              ? "Duplicate UPC — already in the database."
              : extractApiError(err),
          },
        });
        failed++;
      }
    }
    setIngesting(false);
    if (written.length > 0) onItemsWritten?.(written);
    setBanner(
      `Done — wrote ${ok} to the catalog` +
        (skipped > 0 ? `, ${skipped} skipped as duplicates` : "") +
        (failed > 0 ? `, ${failed} failed` : "") +
        "."
    );
    if (ok > 0) {
      setToast({
        message: `Saved — wrote ${ok} record${
          ok === 1 ? "" : "s"
        } to the catalog.`,
        tone: "success",
      });
    } else {
      setToast(null);
    }
  };

  // One-click: write every ready item in the batch.
  const ingestAllReady = () => {
    setStates((s) => {
      const n = { ...s };
      selectableKeys.forEach((k) => {
        n[k] = { ...n[k], selected: true };
      });
      return n;
    });
    void ingestSelected(selectableKeys);
  };

  // ----- single item card ---------------------------------------------------
  const renderItem = ({
    item,
    key,
    i,
  }: {
    item: IngestReviewItem;
    key: string;
    i: number;
  }) => {
    const st = states[key];
    if (!st) return null;
    const proposed = st.effectivePayload;
    const selectable =
      (st.effectiveAction === "create" || st.effectiveAction === "update") &&
      !st.upcConflict;
    const isDuplicate = st.upcConflict && !!st.dupMatch;
    const similar = isDuplicate ? [] : item.existing_matches || [];
    const written = st.outcome?.status === "ok";
    const collapsed = !!st.collapsed;
    const canSelect = selectable || isDuplicate;

    const accent = written
      ? "border-l-green-500"
      : isDuplicate || st.effectiveAction === "blocked"
      ? "border-l-red-500"
      : st.effectiveAction === "update"
      ? "border-l-blue-500"
      : "border-l-green-500";
    const displayName =
      proposed.name || item.name || item.original_input || "Proposed product";
    const displayBrand =
      proposed.brandName || proposed.branded?.owner || item.brand || "";
    const status = itemStatus(st, isDuplicate);

    return (
      <div
        key={key}
        id={`review-item-${i}`}
        className={`scroll-mt-[132px] rounded-lg border border-l-4 shadow-sm ${accent} ${
          written ? "border-green-200 bg-green-50/40" : "bg-white"
        }`}
      >
        {/* Item header: sticky under the group header, large, clickable to
            collapse; the select checkbox and Edit button are separate targets. */}
        <div
          className={`sticky top-[116px] z-[7] flex flex-col gap-2 rounded-t-lg border-b border-gray-100 px-4 py-3 backdrop-blur ${
            written ? "bg-green-50/90" : "bg-white/95"
          }`}
        >
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer"
              checked={!!st.selected}
              disabled={
                !canSelect ||
                ingesting ||
                written ||
                st.discarded ||
                st.retrying
              }
              onChange={(e) => patch(key, { selected: e.target.checked })}
              title={
                isDuplicate
                  ? "Select to bulk-merge with the existing record"
                  : selectable
                  ? "Select to write to the catalog"
                  : "Resolve issues before this can be written"
              }
            />
            <button
              type="button"
              onClick={() => patch(key, { collapsed: !collapsed })}
              className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
              aria-label={collapsed ? "Expand" : "Collapse"}
              title={collapsed ? "Expand" : "Collapse"}
            >
              <span className="text-base leading-none">
                {collapsed ? "▸" : "▾"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => patch(key, { collapsed: !collapsed })}
              className="min-w-0 flex-1 text-left"
            >
              <div className="flex flex-wrap items-center gap-2">
                {st.effectiveAction === "create" && !st.upcConflict && (
                  <Badge tone="green">New</Badge>
                )}
                {st.effectiveAction === "update" && (
                  <Badge tone="blue">
                    {st.mergeApplied ? "Update (merged)" : "Update"}
                  </Badge>
                )}
                {isDuplicate &&
                  (mergeIsNoOp(st.mergeExisting, st.mergeResult?.merged) ? (
                    <Badge tone="blue">Already up to date</Badge>
                  ) : (
                    <Badge tone="red">Duplicate</Badge>
                  ))}
                {st.effectiveAction === "blocked" && !isDuplicate && (
                  <Badge tone="amber">Needs attention</Badge>
                )}
                {written && <Badge tone="green">Written</Badge>}
                {st.edited && <Badge tone="gray">edited</Badge>}
                <span className="truncate text-base font-semibold text-gray-900">
                  {displayName}
                </span>
                {displayBrand && (
                  <span className="truncate text-sm text-gray-500">
                    by {displayBrand}
                  </span>
                )}
              </div>
              <p className={`mt-1 text-sm font-medium ${status.cls}`}>
                {status.text}
              </p>
              {st.recheckMsg && (
                <p className="mt-0.5 text-sm text-gray-500">{st.recheckMsg}</p>
              )}
              {st.retrying && (
                <p className="mt-0.5 text-sm text-amber-600">
                  Re-running research in the background…
                </p>
              )}
              {st.retryError && !st.retrying && (
                <p className="mt-0.5 text-sm text-red-600">
                  Retry: {st.retryError}
                </p>
              )}
            </button>
            {st.discarded ? (
              <button
                type="button"
                onClick={() =>
                  patch(key, { discarded: false, collapsed: false })
                }
                className="flex-shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
              >
                ↩ Restore
              </button>
            ) : (
              !written && (
                <div className="flex flex-shrink-0 items-center gap-2">
                  {retry && (
                    <button
                      type="button"
                      onClick={() => startItemRetry(key, i, item)}
                      disabled={st.retrying}
                      title="Re-run research for this item (e.g. to find missing nutrition/UPC). Runs in the background; the row updates when it finishes."
                      className="inline-flex items-center gap-1 rounded-md border border-amber-500 bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {st.retrying ? "↻ Retrying…" : "↻ Retry"}
                    </button>
                  )}
                  {hasContent(proposed) && (
                    <button
                      type="button"
                      onClick={() => setItemEditing(key, !st.editing)}
                      disabled={st.retrying}
                      className={`rounded-md border px-3 py-1.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                        st.editing
                          ? "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
                          : "border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50"
                      }`}
                    >
                      {st.editing ? "✓ Done" : "✎ Edit"}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={st.retrying}
                    onClick={() =>
                      patch(key, {
                        discarded: true,
                        selected: false,
                        editing: false,
                        collapsed: true,
                      })
                    }
                    title="Throw this item out of the review queue (you can restore it)"
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm font-medium text-gray-500 transition hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    🗑 Discard
                  </button>
                </div>
              )
            )}
          </div>

          {/* Duplicate lens toggle stays in the sticky header so you can flip
              Existing/New/Merged while scrolled down diffing nutrients. */}
          {isDuplicate && st.dupMatch && (
            <div className="flex items-center gap-2 pl-8">
              <LensTabs
                view={st.mergeView || "new"}
                hasMerged={!!st.mergeResult?.merged}
                onView={(v) => setMergeView(key, v)}
                size="sm"
              />
              <span className="text-xs text-gray-400">
                flip to diff Existing vs New
              </span>
            </div>
          )}
        </div>

        {!collapsed && (
          <div className="relative">
            {/* While the AI compare/merge chain OR a post-save re-check runs, dim
                + lock the whole card body and show a clear spinner so it's
                obvious work is happening (esp. on page-load auto-merge) and the
                content doesn't flicker/reload underneath the user. */}
            {(st.mergeRunning || st.rechecking || st.retrying) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-lg bg-white/60 backdrop-blur-[1px]">
                <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-md ring-1 ring-gray-200">
                  <Spinner
                    label={
                      st.mergeRunning
                        ? "AI comparing & merging…"
                        : st.retrying
                        ? "Re-running research…"
                        : "Re-checking concern codes…"
                    }
                  />
                </div>
              </div>
            )}
            <div
              className={`space-y-3 p-4 transition-opacity ${
                st.mergeRunning || st.rechecking || st.retrying
                  ? "pointer-events-none opacity-40"
                  : ""
              }`}
            >
              <ItemIssues
                st={st}
                proposed={proposed}
                reasons={item.decision?.reasons}
                hideDuplicate={isDuplicate}
                onOpenMatch={openMatch}
              />

              {isDuplicate && st.dupMatch ? (
                /* Duplicate → one food, three lenses (Existing / New / Merged) */
                <MergeStudio
                  dupMatch={st.dupMatch}
                  existing={st.mergeExisting}
                  loadingExisting={st.loadingExisting}
                  proposed={proposed}
                  mergeResult={st.mergeResult}
                  mergeRunning={st.mergeRunning}
                  mergeError={st.mergeError}
                  mergeApplied={st.mergeApplied}
                  mergeConcerns={st.mergeConcerns}
                  mergeBlocking={st.mergeBlocking}
                  mergeChainId={mergeChainId}
                  view={st.mergeView || "new"}
                  editing={st.editing}
                  rechecking={st.rechecking}
                  ingesting={ingesting}
                  labelSlot={
                    st.dupMatch?.id || st.targetId ? (
                      // Existing record has an id -> link live against it.
                      <LabelLink
                        api={api}
                        foodId={(st.dupMatch?.id || st.targetId) as string}
                        suggestions={st.labelSuggestions}
                        candidateLabels={item.candidate_labels}
                      />
                    ) : (
                      // No id yet -> stage the choice; linked on write (update).
                      <LabelLink
                        api={api}
                        suggestions={st.labelSuggestions}
                        candidateLabels={item.candidate_labels}
                        value={st.chosenLabels ?? []}
                        onChange={(labels) =>
                          patch(key, { chosenLabels: labels })
                        }
                      />
                    )
                  }
                  onView={(v) => setMergeView(key, v)}
                  onOpenExisting={() => st.dupMatch && openMatch(st.dupMatch)}
                  onChangeProposed={(next) => changePayload(key, next)}
                  onEditingChange={(isEditing) =>
                    setItemEditing(key, isEditing)
                  }
                  onRecheck={() => recheck(key)}
                  onMerge={() => runMerge(item, key)}
                  onOverwrite={() => overwriteExisting(key)}
                  onSkip={() =>
                    patch(key, { collapsed: true, selected: false })
                  }
                  onApply={() => applyMerge(key)}
                />
              ) : (
                /* Proposed record — editable in place */
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  {hasContent(proposed) ? (
                    <>
                      <FoodDetailPanel
                        food={proposed}
                        title={displayName}
                        onChange={(next) => changePayload(key, next)}
                        editing={st.editing}
                        onEditingChange={(isEditing) =>
                          setItemEditing(key, isEditing)
                        }
                        onRecheck={() => recheck(key)}
                        rechecking={st.rechecking}
                        hideEditToggle
                        hideConcernList
                        labelSlot={
                          <LabelLink
                            api={api}
                            suggestions={st.labelSuggestions}
                            candidateLabels={item.candidate_labels}
                            value={st.chosenLabels ?? []}
                            onChange={(labels) =>
                              patch(key, { chosenLabels: labels })
                            }
                          />
                        }
                      />
                      <JsonBlock value={proposed} label="proposed — raw JSON" />
                    </>
                  ) : (
                    <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-700">
                      No proposed record was produced for this item.
                    </div>
                  )}
                </div>
              )}

              {/* Possible existing matches (hidden when we have an exact dup) */}
              {similar.length > 0 && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="text-sm font-semibold text-gray-600">
                    Possible existing matches{" "}
                    <span className="font-normal text-gray-400">
                      (click a name to view full nutrition &amp; portions)
                    </span>
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {similar.slice(0, 8).map((m, mi) => (
                      <li
                        key={mi}
                        className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm"
                      >
                        <button
                          type="button"
                          onClick={() => openMatch(m)}
                          className="text-left font-medium text-indigo-600 underline decoration-dotted hover:text-indigo-800"
                        >
                          {m.name}
                          {m.brand ? ` (${m.brand})` : ""}
                        </button>
                        {m.country && (
                          <span className="text-gray-400">· {m.country}</span>
                        )}
                        {m.product_code && (
                          <span className="font-mono text-gray-400">
                            · UPC {m.product_code}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <GroundingPanel grounding={item.__grounding} />

              {st.outcome && (
                <div
                  className={`text-sm font-medium ${
                    st.outcome.status === "ok"
                      ? "text-green-700"
                      : st.outcome.status === "blocked"
                      ? "text-red-700"
                      : "text-red-600"
                  }`}
                >
                  {st.outcome.status === "ok"
                    ? `✓ Ingested (id ${st.outcome.id})`
                    : `✕ ${st.outcome.error}`}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!reviewItems || reviewItems.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
        Nothing to review yet. Go back to{" "}
        <span className="font-semibold">step 1</span>, add some products, and
        click <span className="font-semibold">Research products</span>.
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-6xl gap-5">
      {/* Side index: jump straight to any record, grouped by status. Kept on
          the right so it shares a side with the food-detail drawer. */}
      <aside className="order-last sticky top-0 hidden max-h-screen w-56 shrink-0 self-start overflow-y-auto py-1 lg:block">
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
              Jump to
            </p>
            <div className="flex gap-1 text-xs">
              <button
                type="button"
                onClick={() => setAllCollapsed(true)}
                className="text-gray-400 underline decoration-dotted hover:text-gray-700"
                title="Collapse all"
              >
                collapse
              </button>
              <span className="text-gray-300">·</span>
              <button
                type="button"
                onClick={() => setAllCollapsed(false)}
                className="text-gray-400 underline decoration-dotted hover:text-gray-700"
                title="Expand all"
              >
                expand
              </button>
            </div>
          </div>
          {GROUP_ORDER.map((gid) => {
            const entries = grouped[gid];
            if (entries.length === 0) return null;
            const meta = GROUP_META[gid];
            return (
              <div key={gid} className="mb-2 last:mb-0">
                <div className="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {meta.title} ({entries.length})
                </div>
                <ul className="mt-0.5">
                  {entries.map((e) => {
                    const est = states[e.key];
                    const nm =
                      est?.effectivePayload.name ||
                      e.item.name ||
                      e.item.original_input ||
                      "Product";
                    return (
                      <li key={e.key}>
                        <button
                          type="button"
                          onClick={() => jumpToIndex(e.i)}
                          className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                          title={nm}
                        >
                          <span
                            className={`h-2 w-2 flex-shrink-0 rounded-full ${meta.dot}`}
                          />
                          <span className="truncate">{nm}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="min-w-0 flex-1 space-y-4">
        <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white/95 p-3 shadow-sm backdrop-blur">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
            <span className="text-base">
              <span className="font-bold">{reviewItems.length}</span> product
              {reviewItems.length === 1 ? "" : "s"}
            </span>
            {summary.ready > 0 && (
              <span className="font-medium text-green-700">
                ✓ {summary.ready} ready
              </span>
            )}
            {summary.duplicates > 0 && (
              <span className="font-medium text-red-700">
                ⛔ {summary.duplicates} already exist
              </span>
            )}
            {summary.attention > 0 && (
              <span className="font-medium text-amber-700">
                ⚠ {summary.attention} need attention
              </span>
            )}
            {summary.written > 0 && (
              <span className="font-medium text-green-700">
                ✅ {summary.written} written
              </span>
            )}
            {selectedCount > 0 && (
              <span className="text-gray-500">· {selectedCount} selected</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <PrimaryButton
              type="button"
              onClick={() => ingestSelected()}
              disabled={ingesting || selectedCount === 0}
            >
              {ingesting
                ? "Writing…"
                : `Write ${selectedCount || ""} selected to catalog`}
            </PrimaryButton>
          </div>
        </div>

        {banner && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800">
            {banner}
          </div>
        )}

        <Toast
          open={!!toast}
          message={toast?.message || ""}
          tone={toast?.tone}
          actionLabel={toast?.write ? "Write now →" : undefined}
          onAction={toast?.write ? () => ingestSelected() : undefined}
          onClose={() => setToast(null)}
          duration={toast?.write ? 0 : 6000}
        />

        {GROUP_ORDER.map((gid) => {
          const entries = grouped[gid];
          if (entries.length === 0) return null;
          const meta = GROUP_META[gid];
          const gKeys = entries.map((e) => e.key);
          const gcollapsed = !!collapsedGroups[gid];
          const allGroupSelected =
            gKeys.length > 0 && gKeys.every((k) => states[k]?.selected);

          return (
            <section
              key={gid}
              className={`rounded-xl border ${meta.ring} shadow-sm`}
            >
              {/* Group header: sticky, big, clickable to collapse; bulk actions */}
              <div className="sticky top-[60px] z-[15] flex flex-wrap items-center gap-3 rounded-t-xl border-b border-black/5 bg-white/85 px-4 py-3 backdrop-blur">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedGroups((c) => ({ ...c, [gid]: !c[gid] }))
                  }
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-black/10 bg-white/70 text-base leading-none text-gray-700">
                    {gcollapsed ? "▸" : "▾"}
                  </span>
                  <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                  <span className={`text-base font-bold ${meta.text}`}>
                    {meta.icon} {meta.title}
                  </span>
                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-sm font-semibold text-gray-700">
                    {entries.length}
                  </span>
                </button>

                {gid === "ready" && (
                  <div className="flex items-center gap-2">
                    <SecondaryButton
                      type="button"
                      disabled={ingesting}
                      onClick={() => toggleGroupSelect(gKeys)}
                    >
                      {allGroupSelected ? "Deselect all" : "Select all"}
                    </SecondaryButton>
                    <PrimaryButton
                      type="button"
                      disabled={ingesting}
                      onClick={ingestAllReady}
                    >
                      {ingesting ? "Writing…" : `Write all ${entries.length}`}
                    </PrimaryButton>
                  </div>
                )}

                {gid === "duplicate" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <SecondaryButton
                      type="button"
                      onClick={() => toggleGroupSelect(gKeys)}
                    >
                      {allGroupSelected ? "Deselect all" : "Select all"}
                    </SecondaryButton>
                    <AccentButton
                      type="button"
                      disabled={
                        merging ||
                        !mergeChainId ||
                        mergeableSelectedDups.length === 0
                      }
                      onClick={mergeSelectedDuplicates}
                    >
                      {merging
                        ? "Merging…"
                        : `✨ AI merge selected${
                            mergeableSelectedDups.length
                              ? ` (${mergeableSelectedDups.length})`
                              : ""
                          }`}
                    </AccentButton>
                    {applicableMerges.length > 0 && (
                      <AccentButton
                        type="button"
                        color="green"
                        onClick={applyAllMerges}
                      >
                        Apply all merges ({applicableMerges.length})
                      </AccentButton>
                    )}
                    {merging && <Spinner label="merging duplicates" />}
                    {autoMerge &&
                      !merging &&
                      dupKeys.some((k) => states[k]?.mergeRunning) && (
                        <Spinner
                          label={`auto-merging ${
                            dupKeys.filter((k) => states[k]?.mergeRunning)
                              .length
                          }…`}
                        />
                      )}
                  </div>
                )}
              </div>

              {!gcollapsed && (
                <div className="space-y-4 p-3">{entries.map(renderItem)}</div>
              )}
            </section>
          );
        })}

        <FoodDetailDrawer
          open={drawer.open}
          onClose={closeDrawer}
          loading={drawer.loading}
          error={drawer.error}
          title={drawer.title}
          subtitle={drawer.subtitle}
          food={drawer.food}
        />
      </div>
    </div>
  );
};

export default ReviewTab;
